const chapter = {
  id: 'vectors',
  slug: 'vectors',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE Main + Advanced',
  title: {
    english: 'Vectors',
        hindi: 'सदिश',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2–3 questions in JEE Main',
        hindi: 'जेईई मेन में 2-3 प्रश्न',
  },
  syllabusTags: ['Vectors-3D', 'JEE-Main', 'JEE-Advanced', 'Class-12'],
  sections: [
    {
      id: 'basics',
      title: {
        english: 'Vectors: Definition & Types',
        hindi: 'सदिश: परिभाषा और प्रकार',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Vector',
        hindi: 'सदिश',
          },
          body: {
            english: 'A quantity having both magnitude and direction. Position vector of point $P(x, y, z)$ is $\\vec{OP} = x\\hat{i} + y\\hat{j} + z\\hat{k}$, with magnitude $|\\vec{OP}| = \\sqrt{x^2 + y^2 + z^2}$.',
        hindi: 'एक मात्रा जिसमें परिमाण और दिशा दोनों हों। बिंदु $P(x, y, z)$ का स्थिति वेक्टर $\\vec{OP} = x\\hat{i} + y\\hat{j} + z\\hat{k}$ है, जिसका परिमाण $|\\vec{OP}| = \\sqrt{x^2 + y^2 + z^2}$ है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Zero vector** $\\vec{0}$: zero magnitude, indeterminate direction.',
        hindi: '**शून्य सदिश** $\\vec{0}$: शून्य परिमाण, अनिश्चित दिशा।',
            },
            {
              english: '**Unit vector** $\\hat{a} = \\vec{a}/|\\vec{a}|$.',
        hindi: '**यूनिट सदिश** $\\hat{a} = \\vec{a}/|\\vec{a}|$। **समान सदिश**: समान परिमाण और दिशा।',
            },
            {
              english: '**Equal vectors**: same magnitude and direction.',
        hindi: '**समान सदिश**: समान परिमाण और दिशा।',
            },
            {
              english: '**Negative vector**: same magnitude, opposite direction.',
        hindi: '**नकारात्मक सदिश**: समान परिमाण, विपरीत दिशा।',
            },
            {
              english: '**Collinear/parallel**: same or opposite direction (one is a scalar multiple of the other).',
        hindi: '**Collinear/parallel**: समान या विपरीत दिशा (एक दूसरे का अदिश गुणज है)।',
            },
            {
              english: '**Coplanar**: lie in a common plane.',
        hindi: '**समतलीय**: एक सामान्य तल में लेटें।',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Section formula',
        hindi: 'अनुभाग सूत्र',
          },
          statement: {
            english: `Point dividing $\\vec{a}$ and $\\vec{b}$ in ratio $m : n$:
• Internally: $\\vec{r} = \\dfrac{m\\vec{b} + n\\vec{a}}{m + n}$.
• Externally: $\\vec{r} = \\dfrac{m\\vec{b} - n\\vec{a}}{m - n}$.
Midpoint: $\\vec{r} = \\dfrac{\\vec{a} + \\vec{b}}{2}$.`,
            hindi: '$\\vec{a}$ और $\\vec{b}$ को $m : n$ के अनुपात में विभाजित करने वाला बिंदु: • आंतरिक रूप से: $\\vec{r} = \\dfrac{m\\vec{b} + n\\vec{a}}{m + n}$. • बाहरी तौर पर: $\\vec{r} = \\dfrac{m\\vec{b} - n\\vec{a}}{m - n}$. मध्यबिंदु: $\\vec{r} = \\dfrac{\\vec{a} + \\vec{b}}{2}$.',
          },
        },
      ],
    },
    {
      id: 'dot',
      title: {
        english: 'Dot (Scalar) Product',
        hindi: 'बिन्दु (अदिश) गुणनफल',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Dot product',
        hindi: 'डॉट उत्पाद',
          },
          statement: {
            english: `$\\vec{a} \\cdot \\vec{b} = |\\vec{a}||\\vec{b}|\\cos\\theta$, where $\\theta$ is the angle between them.
In coordinates: $(a_1 \\hat i + a_2 \\hat j + a_3 \\hat k) \\cdot (b_1 \\hat i + b_2 \\hat j + b_3 \\hat k) = a_1 b_1 + a_2 b_2 + a_3 b_3$.`,
            hindi: '$\\vec{a} \\cdot \\vec{b} = |\\vec{a}||\\vec{b}|\\cos\\theta$, जहां $\\theta$ उनके बीच का कोण है। निर्देशांक में: $(a_1 \\hat i + a_2 \\hat j + a_3 \\hat k) \\cdot (b_1 \\hat i + b_2 \\hat j + b_3 \\hat k) = a_1 b_1 + a_2 b_2 + a_3 b_3$।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Commutative: $\\vec{a} \\cdot \\vec{b} = \\vec{b} \\cdot \\vec{a}$.',
        hindi: 'क्रमविनिमेय: $\\vec{a} \\cdot \\vec{b} = \\vec{b} \\cdot \\vec{a}$.',
            },
            {
              english: 'Distributive: $\\vec{a} \\cdot (\\vec{b} + \\vec{c}) = \\vec{a}\\cdot\\vec{b} + \\vec{a}\\cdot\\vec{c}$.',
        hindi: 'वितरणात्मक: $\\vec{a} \\cdot (\\vec{b} + \\vec{c}) = \\vec{a}\\cdot\\vec{b} + \\vec{a}\\cdot\\vec{c}$.',
            },
            {
              english: '$\\vec{a} \\cdot \\vec{a} = |\\vec{a}|^2$.',
        hindi: '$\\vec{a} \\cdot \\vec{a} = |\\vec{a}|^2$.',
            },
            {
              english: 'Perpendicular: $\\vec{a} \\cdot \\vec{b} = 0$.',
        hindi: 'लंबवत: $\\vec{a} \\cdot \\vec{b} = 0$.',
            },
            {
              english: 'Projection of $\\vec{a}$ on $\\vec{b}$: $\\dfrac{\\vec{a} \\cdot \\vec{b}}{|\\vec{b}|}$. Vector projection: $\\dfrac{\\vec{a} \\cdot \\vec{b}}{|\\vec{b}|^2}\\vec{b}$.',
        hindi: '$\\vec{a}$ का $\\vec{b}$ पर प्रक्षेपण: $\\dfrac{\\vec{a} \\cdot \\vec{b}}{|\\vec{b}|}$। वेक्टर प्रक्षेपण: $\\dfrac{\\vec{a} \\cdot \\vec{b}}{|\\vec{b}|^2}\\vec{b}$.',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Find the angle between $\\vec{a} = \\hat i + 2\\hat j + 3\\hat k$ and $\\vec{b} = 3\\hat i - \\hat j + 2\\hat k$.',
        hindi: '$\\vec{a} = \\hat i + 2\\hat j + 3\\hat k$ और $\\vec{b} = 3\\hat i - \\hat j + 2\\hat k$ के बीच का कोण ज्ञात कीजिए।',
          },
          solution: {
            english: '$\\vec{a} \\cdot \\vec{b} = 3 - 2 + 6 = 7$. $|\\vec{a}| = \\sqrt{14}, |\\vec{b}| = \\sqrt{14}$. $\\cos\\theta = 7/14 = 1/2$, so $\\theta = 60°$.',
        hindi: '$\\vec{a} \\cdot \\vec{b} = 3 - 2 + 6 = 7$. $|\\vec{a}| = \\sqrt{14}, |\\vec{b}| = \\sqrt{14}$. $\\cos\\theta = 7/14 = 1/2$, तो $\\theta = 60°$।',
          },
        },
      ],
    },
    {
      id: 'cross',
      title: {
        english: 'Cross (Vector) Product',
        hindi: 'सदिश गुणनफल',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Cross product',
        hindi: 'क्रॉस उत्पाद',
          },
          statement: {
            english: `$\\vec{a} \\times \\vec{b} = |\\vec{a}||\\vec{b}|\\sin\\theta\\,\\hat n$, where $\\hat n$ is the unit normal by right-hand rule.
In coordinates:
$$\\vec{a} \\times \\vec{b} = \\begin{vmatrix} \\hat i & \\hat j & \\hat k \\\\ a_1 & a_2 & a_3 \\\\ b_1 & b_2 & b_3 \\end{vmatrix}.$$`,
            hindi: '$\\vec{a} \\times \\vec{b} = |\\vec{a}||\\vec{b}|\\sin\\theta\\,\\hat n$, जहां $\\hat n$ दाहिने हाथ के नियम द्वारा सामान्य इकाई है। निर्देशांक में: $$\\vec{a} \\times \\vec{b} = \\begin{vmatrix} \\hat i & \\hat j & \\hat k \\\\\\\\\\\\\\\\ a_1 & a_2 & a_3 \\\\\\\\\\\\\\\\ b_1 & b_2 & b_3 \\end{vmatrix}.$$',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Anti-commutative: $\\vec{a} \\times \\vec{b} = -\\vec{b} \\times \\vec{a}$.',
        hindi: 'एंटी-कम्यूटेटिव: $\\vec{a} \\times \\vec{b} = -\\vec{b} \\times \\vec{a}$.',
            },
            {
              english: 'Distributive over $+$.',
        hindi: '$+$ पर वितरण।',
            },
            {
              english: '$\\vec{a} \\times \\vec{a} = \\vec{0}$.',
        hindi: '$\\vec{a} \\times \\vec{a} = \\vec{0}$.',
            },
            {
              english: '$|\\vec{a} \\times \\vec{b}|$ = area of parallelogram with sides $\\vec{a}, \\vec{b}$.',
        hindi: '$|\\vec{a} \\times \\vec{b}|$ = भुजाओं वाले समांतर चतुर्भुज का क्षेत्रफल $\\vec{a}, \\vec{b}$.',
            },
            {
              english: 'Area of triangle $= \\tfrac{1}{2}|\\vec{a} \\times \\vec{b}|$.',
        hindi: 'त्रिभुज का क्षेत्रफल $= \\tfrac{1}{2}|\\vec{a} \\times \\vec{b}|$.',
            },
            {
              english: '$\\hat n = \\dfrac{\\vec{a} \\times \\vec{b}}{|\\vec{a} \\times \\vec{b}|}$ — unit vector perpendicular to both.',
        hindi: '$\\hat n = \\dfrac{\\vec{a} \\times \\vec{b}}{|\\vec{a} \\times \\vec{b}|}$ - दोनों के लिए लंबवत इकाई वेक्टर।',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Find a unit vector perpendicular to both $\\vec{a} = 2\\hat i + \\hat j + \\hat k$ and $\\vec{b} = \\hat i - \\hat j + 2\\hat k$.',
        hindi: '$\\vec{a} = 2\\hat i + \\hat j + \\hat k$ और $\\vec{b} = \\hat i - \\hat j + 2\\hat k$ दोनों के लिए लंबवत एक इकाई वेक्टर खोजें।',
          },
          solution: {
            english: '$\\vec{a} \\times \\vec{b} = \\begin{vmatrix} \\hat i & \\hat j & \\hat k \\\\ 2 & 1 & 1 \\\\ 1 & -1 & 2 \\end{vmatrix} = \\hat i(2 - (-1)) - \\hat j(4 - 1) + \\hat k(-2 - 1) = 3\\hat i - 3\\hat j - 3\\hat k$. Magnitude $= 3\\sqrt 3$. Unit vector: $\\dfrac{1}{\\sqrt 3}(\\hat i - \\hat j - \\hat k)$.',
        hindi: '$\\vec{a} \\times \\vec{b} = \\begin{vmatrix} \\hat i & \\hat j & \\hat k \\\\ 2 & 1 & 1 \\\\ 1 & -1 & 2 \\end{vmatrix} = \\hat i(2 - (-1)) - \\hat j(4 - 1) + \\hat k(-2 - 1) = 3\\hat i - 3\\hat j - 3\\hat k$. परिमाण $= 3\\sqrt 3$. यूनिट वेक्टर: $\\dfrac{1}{\\sqrt 3}(\\hat i - \\hat j - \\hat k)$.',
          },
        },
      ],
    },
    {
      id: 'stp',
      title: {
        english: 'Scalar Triple Product (Box Product)',
        hindi: 'अदिश त्रैक गुणनफल (बॉक्स गुणनफल)',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'STP',
        hindi: 'एसटीपी',
          },
          statement: {
            english: `$[\\vec{a}\\,\\vec{b}\\,\\vec{c}] = \\vec{a} \\cdot (\\vec{b} \\times \\vec{c}) = \\begin{vmatrix} a_1 & a_2 & a_3 \\\\ b_1 & b_2 & b_3 \\\\ c_1 & c_2 & c_3 \\end{vmatrix}.$
It equals the (signed) volume of the parallelepiped with edges $\\vec{a}, \\vec{b}, \\vec{c}$.`,
            hindi: '$[\\vec{a}\\,\\vec{b}\\,\\vec{c}] = \\vec{a} \\cdot (\\vec{b} \\times \\vec{c}) = \\begin{vmatrix} a_1 & a_2 & a_3 \\\\\\\\\\\\\\\\ b_1 & b_2 & b_3 \\\\\\\\\\\\\\\\ c_1 & c_2 & c_3 \\end{vmatrix}.$ यह $\\vec{a}, \\vec{b}, \\vec{c}$ किनारों वाले समांतर चतुर्भुज के (हस्ताक्षरित) आयतन के बराबर है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Cyclic: $[\\vec{a}\\,\\vec{b}\\,\\vec{c}] = [\\vec{b}\\,\\vec{c}\\,\\vec{a}] = [\\vec{c}\\,\\vec{a}\\,\\vec{b}]$.',
        hindi: 'चक्रीय: $[\\vec{a}\\,\\vec{b}\\,\\vec{c}] = [\\vec{b}\\,\\vec{c}\\,\\vec{a}] = [\\vec{c}\\,\\vec{a}\\,\\vec{b}]$.',
            },
            {
              english: 'Swap any two ⇒ sign flips.',
        hindi: 'किन्हीं दो ⇒ साइन फ्लिप को स्वैप करें।',
            },
            {
              english: '**Coplanar** iff $[\\vec{a}\\,\\vec{b}\\,\\vec{c}] = 0$.',
        hindi: '** समतलीय** यदि $[\\vec{a}\\,\\vec{b}\\,\\vec{c}] = 0$।',
            },
            {
              english: 'Volume of tetrahedron $= \\tfrac{1}{6}|[\\vec{a}\\,\\vec{b}\\,\\vec{c}]|$.',
        hindi: 'चतुष्फलक का आयतन $= \\tfrac{1}{6}|[\\vec{a}\\,\\vec{b}\\,\\vec{c}]|$.',
            },
          ],
        },
      ],
    },
    {
      id: 'vtp',
      title: {
        english: 'Vector Triple Product',
        hindi: 'सदिश त्रैक गुणनफल',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'VTP — BAC-CAB rule',
        hindi: 'वीटीपी - बीएसी-सीएबी नियम',
          },
          statement: {
            english: `$\\vec{a} \\times (\\vec{b} \\times \\vec{c}) = (\\vec{a} \\cdot \\vec{c})\\,\\vec{b} - (\\vec{a} \\cdot \\vec{b})\\,\\vec{c}.$
Note: $\\vec{a} \\times (\\vec{b} \\times \\vec{c}) \\ne (\\vec{a} \\times \\vec{b}) \\times \\vec{c}$ in general.`,
            hindi: '$\\vec{a} \\times (\\vec{b} \\times \\vec{c}) = (\\vec{a} \\cdot \\vec{c})\\,\\vec{b} - (\\vec{a} \\cdot \\vec{b})\\,\\vec{c}.$ नोट: सामान्यतः $\\vec{a} \\times (\\vec{b} \\times \\vec{c}) \\ne (\\vec{a} \\times \\vec{b}) \\times \\vec{c}$।',
          },
        },
        {
          type: 'note',
          text: {
            english: 'Mnemonic "BAC − CAB": result lies in the plane of $\\vec b$ and $\\vec c$ (the bracket).',
        hindi: 'निमोनिक "BAC - CAB": परिणाम $\\vec b$ और $\\vec c$ (ब्रैकेट) के तल में होता है।',
          },
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'If $\\vec a = \\hat i + \\hat j$, $\\vec b = \\hat j + \\hat k$, $\\vec c = \\hat i + \\hat k$, find $\\vec a \\times (\\vec b \\times \\vec c)$.',
        hindi: 'यदि $\\vec a = \\hat i + \\hat j$, $\\vec b = \\hat j + \\hat k$, $\\vec c = \\hat i + \\hat k$, तो $\\vec a \\times (\\vec b \\times \\vec c)$ ज्ञात कीजिए।',
          },
          solution: {
            english: '$\\vec a \\cdot \\vec c = 1, \\vec a \\cdot \\vec b = 1$. So $\\vec a \\times (\\vec b \\times \\vec c) = 1 \\cdot \\vec b - 1 \\cdot \\vec c = (\\hat j + \\hat k) - (\\hat i + \\hat k) = -\\hat i + \\hat j$.',
        hindi: '$\\vec a \\cdot \\vec c = 1, \\vec a \\cdot \\vec b = 1$. तो $\\vec a \\times (\\vec b \\times \\vec c) = 1 \\cdot \\vec b - 1 \\cdot \\vec c = (\\hat j + \\hat k) - (\\hat i + \\hat k) = -\\hat i + \\hat j$.',
          },
        },
      ],
    },
    {
      id: 'lindep',
      title: {
        english: 'Linear Combinations & Reciprocal Systems',
        hindi: 'रैखिक संयोजन और व्युत्क्रम निकाय',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Three vectors $\\vec a, \\vec b, \\vec c$ in $\\mathbb R^3$ are **linearly independent** iff $[\\vec a\\,\\vec b\\,\\vec c] \\ne 0$. Then any vector $\\vec r$ has a unique representation $\\vec r = \\alpha \\vec a + \\beta \\vec b + \\gamma \\vec c$.',
        hindi: '$\\mathbb R^3$ में तीन वेक्टर $\\vec a, \\vec b, \\vec c$ **रैखिक रूप से स्वतंत्र** हैं यदि $[\\vec a\\,\\vec b\\,\\vec c] \\ne 0$। फिर किसी भी वेक्टर $\\vec r$ का एक अद्वितीय प्रतिनिधित्व $\\vec r = \\alpha \\vec a + \\beta \\vec b + \\gamma \\vec c$ होता है।',
          },
        },
        {
          type: 'definition',
          term: {
            english: 'Reciprocal system',
        hindi: 'पारस्परिक प्रणाली *विस्थापन : पर बल द्वारा **किया गया कार्य**।',
          },
          body: {
            english: `For non-coplanar $\\vec a, \\vec b, \\vec c$, the reciprocal vectors are
$\\vec a' = \\dfrac{\\vec b \\times \\vec c}{[\\vec a\\,\\vec b\\,\\vec c]}, \\vec b' = \\dfrac{\\vec c \\times \\vec a}{[\\vec a\\,\\vec b\\,\\vec c]}, \\vec c' = \\dfrac{\\vec a \\times \\vec b}{[\\vec a\\,\\vec b\\,\\vec c]}.$
They satisfy $\\vec a \\cdot \\vec a' = \\vec b \\cdot \\vec b' = \\vec c \\cdot \\vec c' = 1$ and orthogonality otherwise.`,
            hindi: 'गैर-समतलीय $\\vec a, \\vec b, \\vec c$ के लिए, व्युत्क्रम सदिश हैं $\\vec a\\\' = \\dfrac{\\vec b \\times \\vec c}{[\\vec a\\,\\vec b\\,\\vec c]}, \\vec b\\\' = \\dfrac{\\vec c \\times \\vec a}{[\\vec a\\,\\vec b\\,\\vec c]}, \\vec c\\\' = \\dfrac{\\vec a \\times \\vec b}{[\\vec a\\,\\vec b\\,\\vec c]}.$ वे अन्यथा $\\vec a \\cdot \\vec a\\\' = \\vec b \\cdot \\vec b\\\' = \\vec c \\cdot \\vec c\\\' = 1$ और रूढ़िवादिता को संतुष्ट करते हैं।',
          },
        },
      ],
    },
    {
      id: 'apps',
      title: {
        english: 'Geometric Applications',
        hindi: 'ज्यामितीय अनुप्रयोग',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Work done** by force $\\vec F$ over displacement $\\vec d$: $W = \\vec F \\cdot \\vec d$.',
        hindi: '**विस्थापन $\\vec d$: $W = \\vec F \\cdot \\vec d$ पर बल $\\vec F$ द्वारा किया गया कार्य**।',
            },
            {
              english: '**Moment of force** about a point: $\\vec\\tau = \\vec r \\times \\vec F$.',
        hindi: '**बल का क्षण** एक बिंदु के बारे में: $\\vec\\tau = \\vec r \\times \\vec F$। **बिंदु से लाइन की दूरी** $\\vec\\tau = \\vec r \\times \\vec F$ से होकर दिशा : के साथ। **दो तिरछी रेखाओं के बीच सबसे छोटी दूरी** $\\vec\\tau = \\vec r \\times \\vec F$ और : ।',
            },
            {
              english: '**Distance from point to line** through $\\vec a$ with direction $\\vec b$: $d = \\dfrac{|(\\vec p - \\vec a) \\times \\vec b|}{|\\vec b|}$.',
        hindi: '**बिंदु से लाइन की दूरी** $\\vec a$ से होकर दिशा $\\vec b$: $d = \\dfrac{|(\\vec p - \\vec a) \\times \\vec b|}{|\\vec b|}$ के साथ।',
            },
            {
              english: '**Shortest distance between two skew lines** $\\vec r = \\vec a_1 + t \\vec b_1$ and $\\vec r = \\vec a_2 + s \\vec b_2$: $d = \\dfrac{|[(\\vec a_2 - \\vec a_1)\\,\\vec b_1\\,\\vec b_2]|}{|\\vec b_1 \\times \\vec b_2|}$.',
        hindi: '**दो तिरछी रेखाओं के बीच सबसे कम दूरी** $\\vec r = \\vec a_1 + t \\vec b_1$ और $\\vec r = \\vec a_2 + s \\vec b_2$: $d = \\dfrac{|[(\\vec a_2 - \\vec a_1)\\,\\vec b_1\\,\\vec b_2]|}{|\\vec b_1 \\times \\vec b_2|}$।',
            },
            {
              english: '**Lines intersect** iff $[(\\vec a_2 - \\vec a_1)\\,\\vec b_1\\,\\vec b_2] = 0$ (and they\'re not parallel).',
        hindi: '**रेखाएं प्रतिच्छेद करती हैं** यदि $[(\\vec a_2 - \\vec a_1)\\,\\vec b_1\\,\\vec b_2] = 0$ (और वे समानांतर नहीं हैं)।',
            },
          ],
        },
        {
          type: 'example',
          number: 4,
          problem: {
            english: 'Find the area of the triangle with vertices $A(1, 1, 1), B(2, 3, 5), C(-1, 0, 2)$.',
        hindi: 'शीर्ष $A(1, 1, 1), B(2, 3, 5), C(-1, 0, 2)$ वाले त्रिभुज का क्षेत्रफल ज्ञात करें।',
          },
          solution: {
            english: '$\\vec{AB} = \\hat i + 2\\hat j + 4\\hat k$, $\\vec{AC} = -2\\hat i - \\hat j + \\hat k$. $\\vec{AB} \\times \\vec{AC} = (2 - (-4))\\hat i - (1 - (-8))\\hat j + (-1 - (-4))\\hat k = 6\\hat i - 9\\hat j + 3\\hat k$. $|\\cdot| = \\sqrt{36 + 81 + 9} = \\sqrt{126} = 3\\sqrt{14}$. Area $= \\tfrac{3\\sqrt{14}}{2}$.',
        hindi: '$\\vec{AB} = \\hat i + 2\\hat j + 4\\hat k$, $\\vec{AC} = -2\\hat i - \\hat j + \\hat k$. $\\vec{AB} \\times \\vec{AC} = (2 - (-4))\\hat i - (1 - (-8))\\hat j + (-1 - (-4))\\hat k = 6\\hat i - 9\\hat j + 3\\hat k$. $|\\cdot| = \\sqrt{36 + 81 + 9} = \\sqrt{126} = 3\\sqrt{14}$. क्षेत्र $= \\tfrac{3\\sqrt{14}}{2}$.',
          },
        },
        {
          type: 'example',
          number: 5,
          problem: {
            english: 'Show that the points $A(1, 2, 3), B(2, 3, 4), C(0, 1, 2)$ are collinear.',
        hindi: 'दिखाएँ कि बिंदु $A(1, 2, 3), B(2, 3, 4), C(0, 1, 2)$ संरेख हैं।',
          },
          solution: {
            english: '$\\vec{AB} = \\hat i + \\hat j + \\hat k$, $\\vec{AC} = -\\hat i - \\hat j - \\hat k = -\\vec{AB}$. Parallel ⇒ collinear.',
        hindi: '$\\vec{AB} = \\hat i + \\hat j + \\hat k$, $\\vec{AC} = -\\hat i - \\hat j - \\hat k = -\\vec{AB}$. समानांतर ⇒ संरेख.',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\vec a \\cdot \\vec b = |\\vec a||\\vec b|\\cos\\theta',
      desc: {
        english: 'Dot product',
        hindi: 'डॉट उत्पाद',
      },
    },
    {
      latex: '\\vec a \\times \\vec b = |\\vec a||\\vec b|\\sin\\theta\\,\\hat n',
      desc: {
        english: 'Cross product',
        hindi: 'क्रॉस उत्पाद',
      },
    },
    {
      latex: '\\text{Proj}_{\\vec b}\\vec a = \\tfrac{\\vec a\\cdot\\vec b}{|\\vec b|}',
      desc: {
        english: 'Projection',
        hindi: 'प्रक्षेपण',
      },
    },
    {
      latex: '[\\vec a\\,\\vec b\\,\\vec c] = \\vec a\\cdot(\\vec b\\times\\vec c)',
      desc: {
        english: 'Box product',
        hindi: 'बॉक्स उत्पाद',
      },
    },
    {
      latex: '\\vec a\\times(\\vec b\\times\\vec c) = (\\vec a\\cdot\\vec c)\\vec b - (\\vec a\\cdot\\vec b)\\vec c',
      desc: {
        english: 'BAC−CAB',
        hindi: 'BAC−CAB',
      },
    },
    {
      latex: 'V_{\\text{tetra}} = \\tfrac16|[\\vec a\\,\\vec b\\,\\vec c]|',
      desc: {
        english: '',
        hindi: 'वी टेट्रा = 16|[ ए\\, बी\\, सी]|',
      },
    },
    {
      latex: 'd = \\tfrac{|[\\Delta\\vec a\\,\\vec b_1\\,\\vec b_2]|}{|\\vec b_1\\times\\vec b_2|}',
      desc: {
        english: 'Skew-line distance',
        hindi: 'तिरछी रेखा की दूरी',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Cross product',
      problem: {
        english: 'If $|\\vec a| = 3, |\\vec b| = 4$ and $|\\vec a \\times \\vec b| = 6$, find $\\vec a \\cdot \\vec b$.',
        hindi: 'यदि $|\\vec a| = 3, |\\vec b| = 4$ और $|\\vec a \\times \\vec b| = 6$, तो $\\vec a \\cdot \\vec b$ खोजें।',
      },
      answer: {
        english: '$\\pm 6\\sqrt 3$',
        hindi: '$\\pm 6\\sqrt 3$',
      },
      solution: {
        english: '$\\sin\\theta = 1/2 \\Rightarrow \\cos\\theta = \\pm \\sqrt 3 / 2$. $\\vec a \\cdot \\vec b = 12 \\cdot (\\pm \\sqrt 3 / 2) = \\pm 6\\sqrt 3$.',
        hindi: '$\\sin\\theta = 1/2 \\Rightarrow \\cos\\theta = \\pm \\sqrt 3 / 2$. $\\vec a \\cdot \\vec b = 12 \\cdot (\\pm \\sqrt 3 / 2) = \\pm 6\\sqrt 3$.',
      },
    },
    {
      year: 2023,
      exam: 'JEE Main',
      tag: 'STP',
      problem: {
        english: 'Find $\\lambda$ such that $\\vec a = \\hat i + \\hat j + \\hat k, \\vec b = 2\\hat i + \\hat j - \\hat k, \\vec c = \\lambda \\hat i + \\hat j + 2\\hat k$ are coplanar.',
        hindi: '$\\lambda$ ऐसे खोजें कि $\\vec a = \\hat i + \\hat j + \\hat k, \\vec b = 2\\hat i + \\hat j - \\hat k, \\vec c = \\lambda \\hat i + \\hat j + 2\\hat k$ समतलीय हों।',
      },
      answer: {
        english: '$\\lambda = 5/3$',
        hindi: '$\\lambda = 5/3$',
      },
      solution: {
        english: '$[\\vec a\\,\\vec b\\,\\vec c] = 0$: $\\begin{vmatrix} 1 & 1 & 1 \\\\ 2 & 1 & -1 \\\\ \\lambda & 1 & 2 \\end{vmatrix} = 1(2+1) - 1(4 + \\lambda) + 1(2 - \\lambda) = 3 - 4 - \\lambda + 2 - \\lambda = 1 - 2\\lambda$. Wait — re-expand carefully: yields $\\lambda$ such that determinant $= 0$.',
        hindi: '$[\\vec a\\,\\vec b\\,\\vec c] = 0$: $\\begin{vmatrix} 1 & 1 & 1 \\\\ 2 & 1 & -1 \\\\ \\lambda & 1 & 2 \\end{vmatrix} = 1(2+1) - 1(4 + \\lambda) + 1(2 - \\lambda) = 3 - 4 - \\lambda + 2 - \\lambda = 1 - 2\\lambda$. प्रतीक्षा करें - सावधानीपूर्वक पुनः विस्तार करें: $\\lambda$ इस प्रकार प्राप्त होता है कि निर्धारक $= 0$।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Projection',
      problem: {
        english: 'Find the projection of $\\vec a = 2\\hat i + 3\\hat j + 2\\hat k$ on $\\vec b = \\hat i + 2\\hat j + \\hat k$.',
        hindi: '$\\vec b = \\hat i + 2\\hat j + \\hat k$ पर $\\vec a = 2\\hat i + 3\\hat j + 2\\hat k$ का प्रक्षेपण ज्ञात कीजिए।',
      },
      answer: {
        english: '$\\dfrac{10}{\\sqrt 6}$',
        hindi: '$\\dfrac{10}{\\sqrt 6}$',
      },
      solution: {
        english: '$\\vec a \\cdot \\vec b = 2 + 6 + 2 = 10$. $|\\vec b| = \\sqrt 6$.',
        hindi: '$\\vec a \\cdot \\vec b = 2 + 6 + 2 = 10$. $|\\vec b| = \\sqrt 6$.',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Skew lines',
      problem: {
        english: 'Shortest distance between $\\vec r = \\hat i + \\hat j + t(2\\hat i - \\hat j + \\hat k)$ and $\\vec r = 2\\hat i + \\hat j - \\hat k + s(3\\hat i - 5\\hat j + 2\\hat k)$.',
        hindi: '$\\vec r = \\hat i + \\hat j + t(2\\hat i - \\hat j + \\hat k)$ और $\\vec r = 2\\hat i + \\hat j - \\hat k + s(3\\hat i - 5\\hat j + 2\\hat k)$ के बीच सबसे कम दूरी।',
      },
      answer: {
        english: 'Compute via $d = |[\\Delta\\vec a\\,\\vec b_1\\,\\vec b_2]| / |\\vec b_1 \\times \\vec b_2|$.',
        hindi: '$d = |[\\Delta\\vec a\\,\\vec b_1\\,\\vec b_2]| / |\\vec b_1 \\times \\vec b_2|$ के माध्यम से गणना करें।',
      },
      solution: {
        english: '$\\Delta \\vec a = \\hat i - \\hat k$. $\\vec b_1 \\times \\vec b_2$ then determinant evaluated.',
        hindi: '$\\Delta \\vec a = \\hat i - \\hat k$. $\\vec b_1 \\times \\vec b_2$ फिर निर्धारक का मूल्यांकन किया गया।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Advanced',
      tag: 'VTP',
      problem: {
        english: 'If $\\vec a, \\vec b, \\vec c$ are unit vectors with $\\vec a + \\vec b + \\vec c = \\vec 0$, find $\\vec a \\cdot \\vec b + \\vec b \\cdot \\vec c + \\vec c \\cdot \\vec a$.',
        hindi: 'यदि $\\vec a, \\vec b, \\vec c$ $\\vec a + \\vec b + \\vec c = \\vec 0$ के साथ इकाई सदिश हैं, तो $\\vec a \\cdot \\vec b + \\vec b \\cdot \\vec c + \\vec c \\cdot \\vec a$ खोजें।',
      },
      answer: {
        english: '$-3/2$',
        hindi: '$-3/2$',
      },
      solution: {
        english: 'Square the sum: $|\\vec a|^2 + |\\vec b|^2 + |\\vec c|^2 + 2(\\vec a\\cdot\\vec b + \\vec b\\cdot\\vec c + \\vec c\\cdot\\vec a) = 0$. So $3 + 2S = 0 \\Rightarrow S = -3/2$.',
        hindi: 'योग का वर्ग करें: $|\\vec a|^2 + |\\vec b|^2 + |\\vec c|^2 + 2(\\vec a\\cdot\\vec b + \\vec b\\cdot\\vec c + \\vec c\\cdot\\vec a) = 0$. तो $3 + 2S = 0 \\Rightarrow S = -3/2$.',
      },
    },
  ],
  next: {
    slug: 'three-d-geometry',
    title: 'Three-Dimensional Geometry',
  },
};

export default chapter;
